import { AccountSelector, Badge, TagPill } from '@components'
import { getExpenseClaimExpensePageUrl } from '@constants'
import { ExpenseClaim, ExpenseClaimLine } from '@query'
import { TagSelector } from '@root/containers/TagSelector/TagSelector'
import { formatEurosWithOpts } from '@utils/formatCurrency'
import { TFunction } from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Card, CardHeader, CardTitle, Help, TextLink } from './shared'

type Props = {
  slug: string
  claimId: number
  claim: ExpenseClaim
  lines: ExpenseClaimLine[]
  showHelp: boolean
  showMissingAccounts: boolean
  onUpdateLine: (payload: {
    lineId: number
    account_id?: number | null
    tag_ids?: number[]
  }) => void
}

export const lineNeedsAccount = (line: ExpenseClaimLine) => !line.account && line.amount !== 0

const lineMeta = (line: ExpenseClaimLine, t: TFunction) => {
  const parts: string[] = []
  if (line.distance_source === 'MANUAL') {
    parts.push(t('travelAndExpenses.summary.editedManually'))
    if (line.route_distance_km) {
      parts.push(
        t('travelAndExpenses.summary.originalKm', {
          km: line.route_distance_km.replace('.', ',')
        })
      )
    }
  }
  return parts.join(' · ')
}

export const ClaimSummaryLinesTable: React.FC<Props> = ({
  slug,
  claimId,
  claim,
  lines,
  showHelp,
  showMissingAccounts,
  onUpdateLine
}) => {
  const [t] = useTranslation()
  const claimTagName = claim.tags[0]?.name
  const linesTotal = lines.reduce((sum, line) => sum + line.amount, 0)

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t('travelAndExpenses.summary.lines', { count: lines.length })}</CardTitle>
          {showHelp && (
            <Help>
              {claimTagName
                ? t('travelAndExpenses.summary.linesHelp', { tag: claimTagName })
                : t('travelAndExpenses.summary.linesHelpNoClaimTag')}
            </Help>
          )}
        </div>
      </CardHeader>
      <Scroll>
        <Table>
          <Head>
            <span>{t('travelAndExpenses.summary.linesColumnDescription')}</span>
            <span>{t('travelAndExpenses.summary.linesColumnBasis')}</span>
            <span>{t('travelAndExpenses.summary.linesColumnAccount')}</span>
            <span>{t('travelAndExpenses.summary.linesColumnTag')}</span>
            <AmountHead>{t('travelAndExpenses.summary.linesColumnAmount')}</AmountHead>
          </Head>
          {lines.map(line => {
            const meta = lineMeta(line, t)
            return (
              <Row key={line.id}>
                <DescriptionCell>
                  <DescriptionTitle>{line.description}</DescriptionTitle>
                  {meta && <Help>{meta}</Help>}
                  {line.distance_source === 'ROUTE' && (
                    <BadgeRow>
                      <Badge type="success">
                        {t('travelAndExpenses.summary.calculatedFromRoute')}
                      </Badge>
                    </BadgeRow>
                  )}
                  {line.expense_id != null && (
                    <TextLink to={getExpenseClaimExpensePageUrl(slug, claimId, line.expense_id)}>
                      {t('travelAndExpenses.summary.expensesLine')}
                    </TextLink>
                  )}
                </DescriptionCell>
                <Cell>{line.basis || '—'}</Cell>
                <Cell>
                  {claim.can_approve ? (
                    <AccountSelector
                      name={`line-account-${line.id}`}
                      label=""
                      placeholder={t('travelAndExpenses.summary.accountSearch')}
                      value={line.account?.id}
                      allowCreate={false}
                      errors={
                        showMissingAccounts && lineNeedsAccount(line)
                          ? { message: t('travelAndExpenses.summary.accountRequired') }
                          : undefined
                      }
                      onChange={accountId =>
                        onUpdateLine({ lineId: line.id, account_id: accountId })
                      }
                    />
                  ) : line.account ? (
                    `${line.account.number} ${line.account.name}`
                  ) : (
                    t('travelAndExpenses.summary.noAccount')
                  )}
                </Cell>
                <Cell>
                  {claim.can_approve ? (
                    <TagSelector
                      value={line.tags.map(tag => tag.id)}
                      onChange={tagIds => onUpdateLine({ lineId: line.id, tag_ids: tagIds })}
                    />
                  ) : (
                    <TagRow>
                      {line.tags.map(tag => (
                        <TagPill
                          key={tag.id}
                          tag={{
                            id: tag.id,
                            name: tag.name ?? '',
                            color: tag.color ?? undefined
                          }}
                        />
                      ))}
                    </TagRow>
                  )}
                </Cell>
                <AmountCell>{formatEurosWithOpts(line.amount)}</AmountCell>
              </Row>
            )
          })}
          <Foot>
            <span>{t('travelAndExpenses.summary.linesTotal')}</span>
            <AmountCell>{formatEurosWithOpts(linesTotal)}</AmountCell>
          </Foot>
        </Table>
      </Scroll>
    </Card>
  )
}

const Scroll = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  min-width: 0;
  max-width: 100%;
`

const Table = styled.div`
  min-width: ${({ theme }) => theme.layout.summaryLinesTableMin}rem;
`

const Head = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1.3fr 1fr ${({ theme }) =>
      theme.layout.summaryLinesAmountColumn}rem;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.metalGray};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.caps};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1.3fr 1fr ${({ theme }) =>
      theme.layout.summaryLinesAmountColumn}rem;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
  align-items: center;
`

const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const Cell = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  min-width: 0;
`

const DescriptionCell = styled(Cell)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const DescriptionTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const AmountHead = styled.span`
  text-align: right;
`

const AmountCell = styled.div`
  text-align: right;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-variant-numeric: tabular-nums;
`

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const BadgeRow = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
`
